import React from 'react'
import { Button } from '../ui/Button'
import { useAuth } from '../AuthProvider'
import { useI18n } from '../../i18n'
import { GovernanceInlineFormPanel } from '../governance/GovernanceInlineFormPanel'
import type { InformationSystemSummary } from '../../services/informationSystemsApi'
import { fetchOrgUsers, type OrgUserOption } from '../../services/informationSystemsApi'
import { LIFECYCLE_OPTIONS } from './informationSystemConstants'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'

export type InformationSystemFormValues = {
  name: string
  code: string
  description: string
  businessOwner: string
  technicalOwner: string
  businessOwnerUserId: string
  technicalOwnerUserId: string
  environment: string
  criticality: string
  lifecycleStatus: string
}

const emptyValues: InformationSystemFormValues = {
  name: '',
  code: '',
  description: '',
  businessOwner: '',
  technicalOwner: '',
  businessOwnerUserId: '',
  technicalOwnerUserId: '',
  environment: '',
  criticality: '',
  lifecycleStatus: 'DRAFT',
}

export function systemToFormValues(s?: Partial<InformationSystemSummary> | null): InformationSystemFormValues {
  if (!s) return { ...emptyValues }
  return {
    name: s.name || '',
    code: s.code || '',
    description: s.description || '',
    businessOwner: s.businessOwner || '',
    technicalOwner: s.technicalOwner || '',
    businessOwnerUserId: s.businessOwnerUserId || '',
    technicalOwnerUserId: s.technicalOwnerUserId || '',
    environment: s.environment || '',
    criticality: s.criticality || '',
    lifecycleStatus: s.lifecycleStatus || 'DRAFT',
  }
}

export function formValuesToPayload(values: InformationSystemFormValues) {
  return {
    name: values.name,
    code: values.code || null,
    description: values.description || null,
    businessOwner: values.businessOwner || null,
    technicalOwner: values.technicalOwner || null,
    businessOwnerUserId: values.businessOwnerUserId || null,
    technicalOwnerUserId: values.technicalOwnerUserId || null,
    environment: values.environment || null,
    criticality: values.criticality || null,
    lifecycleStatus: values.lifecycleStatus || 'DRAFT',
  }
}

interface InformationSystemFormProps {
  initial?: Partial<InformationSystemSummary> | null
  submitting?: boolean
  error?: string | null
  onSubmit: (values: InformationSystemFormValues) => void
  onCancel?: () => void
}

function FormField({
  id,
  label,
  required,
  children,
  className,
}: {
  id: string
  label: string
  required?: boolean
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={`${listStyles.formField} ${className ?? ''}`.trim()}>
      <label className={listStyles.formFieldLabel} htmlFor={id}>
        {label}
        {required ? ' *' : ''}
      </label>
      {children}
    </div>
  )
}

export default function InformationSystemForm({
  initial,
  submitting,
  error,
  onSubmit,
  onCancel,
}: InformationSystemFormProps) {
  const { t } = useI18n()
  const { token } = useAuth()
  const [values, setValues] = React.useState<InformationSystemFormValues>(() => systemToFormValues(initial))
  const [orgUsers, setOrgUsers] = React.useState<OrgUserOption[]>([])
  const isEdit = Boolean(initial?.id)
  const formId = isEdit ? 'information-system-edit-form' : 'information-system-create-form'

  React.useEffect(() => {
    setValues(systemToFormValues(initial))
  }, [initial?.id])

  React.useEffect(() => {
    if (!token) return
    fetchOrgUsers(token).then(setOrgUsers).catch(() => setOrgUsers([]))
  }, [token])

  const set = (key: keyof InformationSystemFormValues, v: string) =>
    setValues((prev) => ({ ...prev, [key]: v }))

  const title = isEdit
    ? t('assets.informationSystems.editTitle', 'Edit Information System')
    : t('assets.informationSystems.createTitle', 'Create Information System')

  const footer = (
    <>
      {onCancel ? (
        <Button type="button" color="outline" size="small" onClick={onCancel} disabled={submitting}>
          {t('common.cancel', 'Cancel')}
        </Button>
      ) : null}
      <Button
        type="submit"
        form={formId}
        color="brand"
        size="small"
        disabled={submitting || !values.name.trim()}
      >
        {submitting ? t('common.saving', 'Saving…') : isEdit ? t('common.save', 'Save') : t('common.create', 'Create')}
      </Button>
    </>
  )

  const formBody = (
    <>
      <div className={listStyles.formGrid2}>
        <FormField id="is-form-name" label={t('assets.informationSystems.fields.name', 'Name')} required>
          <input
            id="is-form-name"
            type="text"
            className={listStyles.formInput}
            value={values.name}
            onChange={(e) => set('name', e.target.value)}
            required
          />
        </FormField>
        <FormField id="is-form-code" label={t('assets.informationSystems.fields.code', 'Code / System ID')}>
          <input
            id="is-form-code"
            type="text"
            className={listStyles.formInput}
            value={values.code}
            onChange={(e) => set('code', e.target.value)}
          />
        </FormField>
      </div>

      <FormField id="is-form-description" label={t('assets.informationSystems.fields.description', 'Description')}>
        <textarea
          id="is-form-description"
          rows={3}
          className={listStyles.formTextarea}
          value={values.description}
          onChange={(e) => set('description', e.target.value)}
        />
      </FormField>

      <div className={listStyles.formGrid2}>
        <FormField id="is-form-business-owner" label={t('assets.informationSystems.fields.businessOwner', 'Business owner')}>
          <input
            id="is-form-business-owner"
            type="text"
            className={listStyles.formInput}
            value={values.businessOwner}
            onChange={(e) => set('businessOwner', e.target.value)}
          />
          {orgUsers.length > 0 ? (
            <select
              id="is-form-business-owner-user"
              className={listStyles.formInput}
              value={values.businessOwnerUserId}
              onChange={(e) => set('businessOwnerUserId', e.target.value)}
            >
              <option value="">{t('assets.owners.selectUser', 'Link to user (optional)')}</option>
              {orgUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.username || u.email}
                </option>
              ))}
            </select>
          ) : null}
        </FormField>

        <FormField id="is-form-technical-owner" label={t('assets.informationSystems.fields.technicalOwner', 'Technical owner')}>
          <input
            id="is-form-technical-owner"
            type="text"
            className={listStyles.formInput}
            value={values.technicalOwner}
            onChange={(e) => set('technicalOwner', e.target.value)}
          />
          {orgUsers.length > 0 ? (
            <select
              id="is-form-technical-owner-user"
              className={listStyles.formInput}
              value={values.technicalOwnerUserId}
              onChange={(e) => set('technicalOwnerUserId', e.target.value)}
            >
              <option value="">{t('assets.owners.selectUser', 'Link to user (optional)')}</option>
              {orgUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.username || u.email}
                </option>
              ))}
            </select>
          ) : null}
        </FormField>

        <FormField id="is-form-environment" label={t('assets.informationSystems.fields.environment', 'Environment')}>
          <input
            id="is-form-environment"
            type="text"
            className={listStyles.formInput}
            value={values.environment}
            onChange={(e) => set('environment', e.target.value)}
          />
        </FormField>

        <FormField id="is-form-criticality" label={t('assets.informationSystems.fields.criticality', 'Criticality')}>
          <input
            id="is-form-criticality"
            type="text"
            className={listStyles.formInput}
            value={values.criticality}
            onChange={(e) => set('criticality', e.target.value)}
          />
        </FormField>

        <FormField
          id="is-form-lifecycle"
          label={t('assets.informationSystems.fields.lifecycleStatus', 'Lifecycle status')}
        >
          <select
            id="is-form-lifecycle"
            className={listStyles.formInput}
            value={values.lifecycleStatus || 'DRAFT'}
            onChange={(e) => set('lifecycleStatus', e.target.value)}
          >
            {LIFECYCLE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {t(`assets.lifecycle.${s}`, s)}
              </option>
            ))}
          </select>
        </FormField>
      </div>

      {error ? <div className={listStyles.formError}>{error}</div> : null}
    </>
  )

  if (!onCancel) {
    return (
      <form
        id={formId}
        className={listStyles.inlineFormBody}
        onSubmit={(e) => {
          e.preventDefault()
          onSubmit(values)
        }}
      >
        {formBody}
        <div className={listStyles.inlineFormActions}>{footer}</div>
      </form>
    )
  }

  return (
    <GovernanceInlineFormPanel title={title} onClose={onCancel} footer={footer}>
      <form
        id={formId}
        onSubmit={(e) => {
          e.preventDefault()
          onSubmit(values)
        }}
      >
        {formBody}
      </form>
    </GovernanceInlineFormPanel>
  )
}
